import React, { useState, useRef, useCallback } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MicIcon from "@mui/icons-material/Mic";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useDAW } from "./DAWProvider";
import StepSequencer from "./StepSequencer";
import PianoRoll from "./PianoRoll";
import PianoKeyboard from "./PianoKeyboard";
import useKeyboardNotes from "./useKeyboardNotes";
import { INSTRUMENT_LIST, isInstrumentLoading } from "./soundfontLoader";

const AUDIO_ACCEPT = ".wav,.mp3,.ogg,.m4a,.aac,.flac,.webm";
const SAMPLE_ACCEPT = ".wav,.mp3,.ogg,.m4a,.aac,.flac,.webm";

// Group instruments by category for the dropdown
const CATEGORIES = [...new Set(INSTRUMENT_LIST.map((i) => i.category))];

export default function BottomPanel() {
  const {
    selectedTrackId,
    audioTracks,
    instrumentTracks,
    addAudioTrack,
    importAudioFile,
    audioCtxRef,
    ensureAudioContext,
    isPlaying,
    triggerInstrumentNote,
    setInstrumentSample,
    setInstrumentType,
    toggleInstrumentTrackRecordArm,
    recordMidiNoteOn,
    recordMidiNoteOff,
  } = useDAW();

  const importInputRef = useRef(null);
  const sampleInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [trackName, setTrackName] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const showSequencer = selectedTrackId === "drums";
  const selectedInstrument = instrumentTracks.find((t) => t.id === selectedTrackId);
  const showPianoRoll = !!selectedInstrument;
  const isArmed = selectedInstrument?.recordArmed || false;
  const anyArmed = instrumentTracks.some((t) => t.recordArmed);

  // Trigger note + record note-on (records to all armed tracks)
  const handleKeyboardNoteOn = useCallback((midi) => {
    triggerInstrumentNote(midi);
    recordMidiNoteOn(midi);
  }, [triggerInstrumentNote, recordMidiNoteOn]);

  // Note-off for recording duration
  const handleKeyboardNoteOff = useCallback((midi) => {
    recordMidiNoteOff(midi);
  }, [recordMidiNoteOff]);

  // Computer keyboard → MIDI (only when instrument track selected)
  const { baseOctave } = useKeyboardNotes(handleKeyboardNoteOn, showPianoRoll, handleKeyboardNoteOff);

  // Virtual keyboard click
  const handleNoteDown = useCallback((midi) => {
    triggerInstrumentNote(midi);
    recordMidiNoteOn(midi);
    // Auto noteOff after a short delay for click-based playing
    setTimeout(() => recordMidiNoteOff(midi), 500);
  }, [triggerInstrumentNote, recordMidiNoteOn, recordMidiNoteOff]);

  const handleImportSample = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedInstrument) return;
    await ensureAudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const ctx = audioCtxRef.current;
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    setInstrumentSample(selectedInstrument.id, decoded);
    e.target.value = "";
  }, [selectedInstrument, ensureAudioContext, audioCtxRef, setInstrumentSample]);

  const handleInstrumentChange = useCallback((e) => {
    if (!selectedInstrument) return;
    setInstrumentType(selectedInstrument.id, e.target.value);
  }, [selectedInstrument, setInstrumentType]);

  const startRecording = useCallback(async () => {
    await ensureAudioContext();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const arrayBuffer = await blob.arrayBuffer();
        const ctx = audioCtxRef.current;
        const decoded = await ctx.decodeAudioData(arrayBuffer);
        addAudioTrack(trackName || undefined, decoded);
        setTrackName("");
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Mic access denied:", err);
    }
  }, [ensureAudioContext, audioCtxRef, addAudioTrack, trackName]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const panelLabel = showPianoRoll
    ? "Piano Roll"
    : showSequencer
    ? "Step Sequencer"
    : "Record";

  const currentInstrumentType = selectedInstrument?.instrumentType || "acoustic_grand_piano";
  const instrumentLoading = isInstrumentLoading(currentInstrumentType);

  return (
    <div className={`daw-bottom-panel${isOpen ? " daw-bottom-panel--open" : ""}`}>
      {/* Toggle handle */}
      <button className="daw-bottom-panel__handle" onClick={() => setIsOpen(!isOpen)}>
        <span className="daw-bottom-panel__handle-label">
          {panelLabel}
        </span>
        {isOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />}
      </button>

      {/* Panel content */}
      <div className="daw-bottom-panel__content">
        {showSequencer && <StepSequencer />}

        {showPianoRoll && (
          <div className="daw-bottom-panel__piano-section">
            {/* Instrument info bar */}
            <div className="daw-bottom-panel__instrument-bar">
              <button
                className={`daw-bottom-panel__midi-rec-btn${isArmed ? " daw-bottom-panel__midi-rec-btn--active" : ""}`}
                onClick={() => toggleInstrumentTrackRecordArm(selectedInstrument.id)}
                title={isArmed ? "Disarm recording" : "Arm recording (record notes while playing)"}
              >
                <FiberManualRecordIcon sx={{ fontSize: 14 }} />
                {isArmed ? "REC" : "Rec"}
              </button>
              <select
                className="daw-bottom-panel__instrument-select"
                value={selectedInstrument.customSampleBuffer ? "__custom__" : currentInstrumentType}
                onChange={handleInstrumentChange}
              >
                {selectedInstrument.customSampleBuffer && (
                  <option value="__custom__">Custom Sample</option>
                )}
                {CATEGORIES.map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {INSTRUMENT_LIST.filter((i) => i.category === cat).map((inst) => (
                      <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {instrumentLoading && (
                <span className="daw-bottom-panel__loading">Loading...</span>
              )}
              <button
                className="daw-bottom-panel__sample-btn"
                onClick={() => sampleInputRef.current?.click()}
                title="Upload your own sound sample"
              >
                <FileUploadIcon sx={{ fontSize: 14 }} /> Custom Sample
              </button>
              <input
                ref={sampleInputRef}
                type="file"
                accept={SAMPLE_ACCEPT}
                style={{ display: "none" }}
                onChange={handleImportSample}
              />
              {anyArmed && !isPlaying && (
                <span className="daw-bottom-panel__hint">
                  Press Play to start recording notes
                </span>
              )}
              {anyArmed && isPlaying && (
                <span className="daw-bottom-panel__hint daw-bottom-panel__hint--recording">
                  Recording... play notes on the keyboard
                </span>
              )}
            </div>

            {/* Piano roll editor */}
            <PianoRoll track={selectedInstrument} />

            {/* Playable keyboard */}
            <PianoKeyboard onNoteDown={handleNoteDown} baseOctave={baseOctave} />
          </div>
        )}

        {/* Record section */}
        <div className="daw-bottom-panel__record">
          {!isRecording ? (
            <div className="daw-bottom-panel__record-row">
              <input
                className="daw-bottom-panel__name-input"
                placeholder="Track name..."
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
              />
              <button
                className="daw-bottom-panel__record-btn"
                onClick={startRecording}
                title="Record audio track"
              >
                <MicIcon sx={{ fontSize: 16 }} /> Record Track
              </button>
              <button
                className="daw-bottom-panel__import-btn"
                onClick={() => importInputRef.current?.click()}
                title="Import audio file"
              >
                <AudioFileIcon sx={{ fontSize: 16 }} /> Import File
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept={AUDIO_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  for (const file of Array.from(e.target.files || [])) {
                    await importAudioFile(file);
                  }
                  e.target.value = "";
                }}
              />
              {isPlaying && !anyArmed && (
                <span className="daw-bottom-panel__hint">
                  Tip: Hit play first, then record to lay vocals over the beat
                </span>
              )}
            </div>
          ) : (
            <button
              className="daw-bottom-panel__record-btn daw-bottom-panel__record-btn--recording"
              onClick={stopRecording}
            >
              <FiberManualRecordIcon sx={{ fontSize: 14 }} /> Recording... tap to stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
